import React, { useEffect, useMemo, useState } from "react";
import Avatar from "@mui/material/Avatar";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import moment from "moment";
import { IconButton, Tooltip } from "@mui/material";
import ApiMethods from "../../../../ApiManager/ApiMethods";
import DeletePopUp from "./DeletePopUp";
import PopupTest from "./PopUpTest";
import QuickAddMemberPanel from "./QuickAddMemberPanel";
import { useAlert } from "../../../context/AlertContext";
import { refreshClientPackages } from "../../../data/membersStore";
import useLockBodyScroll from "../../../hooks/useLockBodyScroll";
import { getSlotCapacityRules } from "../../../../common/utils/capacity";
import { WEEKDAY_SHORT } from "../../../../common/utils/weekdayShort";

// Tablet/Desktop gets the compact single-column redesign; Mobile keeps the
// existing bottom-sheet popup untouched. Same breakpoint used by
// NewAppointmentFlow's useIsDesktopTablet.
const useIsDesktopTablet = () => {
  const [isDesktopTablet, setIsDesktopTablet] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsDesktopTablet(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isDesktopTablet;
};

const PopupCard = ({
  setUsersPopuop,
  appointment,
  setActiveAppointment,
  setActiveClientInfo,
  setBookingAdded,
  onEditAppointment,
  onSelectMemberWeek,
}) => {
  const bookingClients = appointment?.bookings_client || [];
  // A no-show still consumes the member's session and counts toward capacity (see
  // `deleteBookings` in auth.ts — only "canceled" is excluded from the backend's
  // used_bookings/capacity query), so `bookingClients`/`usedBookings` above are left as the
  // full, unfiltered list on purpose. But once a trainer has marked someone no-show there's
  // nothing left to view/edit/remove for that booking, so it shouldn't keep occupying a row in
  // this *visible* roster — same as "Otkazano" already disappears once confirmed.
  const visibleBookingClients = bookingClients.filter(
    (client) => client.status !== "noshow"
  );
  const [openDeletePopUp, setOpenDeletePopUp] = useState(false);
  const { showAlert, closeAlert } = useAlert();
  const isDesktopTablet = useIsDesktopTablet();
  // Mount-gated by the parent (`{openUsersPopUp != null && <PopupCard/>}`).
  useLockBodyScroll();

  // View Profile reuses the exact same member edit popup as Members → Edit
  // (PopUpTest.jsx), fed with the full client record (booking rows only
  // carry a lightweight subset), so photo/nickname/email/phone/address/notes
  // etc. all show the member's real, current data.
  const [profileClient, setProfileClient] = useState(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (profileClient) {
          setProfileClient(null);
        } else {
          setUsersPopuop(null);
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [setUsersPopuop, profileClient]);

  // PopUpTest.jsx calls setActiveClientInformation(mergedUpdatedClient) and
  // then refresh()/openPopup() (which clears it back to null) all within
  // the same synchronous save handler. React 18 batches all of those into a
  // single render, so a useEffect watching `profileClient` never actually
  // observes the intermediate "just saved" value — it jumps straight from
  // the pre-edit client to null. Patching the still-open Booked Members
  // list has to happen synchronously, inside this setter itself, using the
  // value PopUpTest just handed us, not via an effect reacting to state.
  const applyProfileClientUpdate = (next) => {
    setProfileClient(next);
    if (!next) return;
    setUsersPopuop((prevAppointment) => {
      if (!prevAppointment) return prevAppointment;
      const nextBookingsClient = prevAppointment.bookings_client.map((bc) =>
        bc.client_id === next.id
          ? {
              ...bc,
              first_name: next.first_name,
              last_name: next.last_name,
              gender: next.gender,
            }
          : bc
      );
      return { ...prevAppointment, bookings_client: nextBookingsClient };
    });
  };

  const [clientToDelete, setClientToDelete] = useState(null);
  const [onConfirmFunc, setOnConfirmFunc] = useState(null);

  const handleDeletePopup = () => {
    return (
      <DeletePopUp
        onConfirm={onConfirmFunc}
        client={clientToDelete}
        message="Da li ste sigurni da želite da obrišete ovaj termin?"
        options={[
          { value: "canceled", label: "Otkazano" },
          { value: "noshow", label: "Nije se pojavio/la" },
        ]}
        closePopUp={() => {
          setOpenDeletePopUp(false);
          setClientToDelete(null);
          setOnConfirmFunc(null);
        }}
      />
    );
  };

  const startDate = new Date(appointment.start);
  const endDate = new Date(appointment.end);
  const startOfToday = moment().startOf("day");
  const isPastDayAndHour = moment(startDate)
    .startOf("hour")
    .isBefore(moment().startOf("hour"));
  const isToday = moment(startDate).isSame(startOfToday, "day");

  const dayOfWeek = startDate.toLocaleString("sr-Latn", {
    weekday: "long",
  });

  const formattedDate = `${
    startDate.getMonth() + 1
  }/${startDate.getDate()}/${startDate.getFullYear()}`;
  const startTime = `${startDate.getHours()}:${
    startDate.getMinutes() < 10 ? "0" : ""
  }${startDate.getMinutes()}`;
  const endTime = `${endDate.getHours()}:${
    endDate.getMinutes() < 10 ? "0" : ""
  }${endDate.getMinutes()}`;

  const weekdayShort = WEEKDAY_SHORT[(startDate.getDay() + 6) % 7];
  const usedBookings = appointment.used_bookings ?? bookingClients.length;
  const maxBookings = appointment.max_bookings || 7;
  // The slot's normal (User App) capacity — 6 for a morning slot, 7 for afternoon/evening —
  // derived from its hour since every slot's stored max_bookings is always the absolute
  // ceiling (7), not the normal one.
  const normalCapacity = getSlotCapacityRules(startDate.getHours()).normalCapacity;
  const isNormallyFull = usedBookings >= normalCapacity;

  // "Dodaj vežbača · Čet 10 · 10:00" — expands this same card into a quick member search
  // (QuickAddMemberPanel below) for this exact slot, so the label always names the slot the
  // trainer is actually looking at instead of a generic "Dodaj vežbača". Same capacity
  // (normal, not the absolute ceiling) and past-slot rules as every other action here — a
  // trainer can't add a member to a slot that's already at its normal capacity or already over,
  // so the button is hidden entirely rather than shown disabled (there's nothing left to do with
  // it, and "Nema slobodnih mesta" right above already explains the capacity case).
  const addMemberSlotLabel = `${weekdayShort} ${startDate.getDate()} · ${startTime}`;
  const addMemberDisabled = isPastDayAndHour || isNormallyFull;

  // "Dodaj vežbača" no longer opens the full New Appointment popup — clicking it now expands
  // this same card downward to reveal a compact search + one eligible-member result
  // (QuickAddMemberPanel). `quickAddKey` is bumped on every open so the panel remounts with a
  // blank search each time, instead of carrying over whatever was typed the previous time it
  // was opened.
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddKey, setQuickAddKey] = useState(0);
  const openQuickAdd = () => {
    setQuickAddKey((k) => k + 1);
    setQuickAddOpen(true);
  };
  const closeQuickAdd = () => setQuickAddOpen(false);
  // A member already booked into *this* slot (any status — no point offering to double-book
  // someone who's already on the list, noshow included) must not show up as a quick-add result.
  const bookedClientIds = useMemo(
    () => new Set(bookingClients.map((c) => c.client_id)),
    [bookingClients]
  );
  const handleQuickAddSuccess = () => {
    closeQuickAdd();
    // Same refresh SchedulerTest.jsx's own delete/edit paths already use — refetches bookings
    // and resyncs this same popup's `appointment` prop to the fresh counts/list, no reopen.
    setBookingAdded();
  };

  // Shared between renderMobileCard/renderDesktopCard below — identical markup either way, so
  // this stays a single implementation instead of two copies drifting apart. Hidden entirely
  // (not just the button) once the slot is past or already at normal capacity, same rule as
  // before — there's nothing left to add here (point 12/14: derived live off `appointment`, so
  // removing a member elsewhere makes the button reappear automatically, no refresh needed).
  const renderAddMemberSection = () => {
    if (addMemberDisabled) return null;
    return (
      <>
        {!quickAddOpen && (
          <button
            type="button"
            className="hf-bp-add-member-btn"
            onClick={openQuickAdd}
          >
            <AddIcon sx={{ fontSize: 16 }} />
            <span className="hf-bp-add-member-label">Dodaj vežbača</span>
            <span className="hf-bp-add-member-slot">· {addMemberSlotLabel}</span>
          </button>
        )}
        {/* Always mounted once reachable (not just while `quickAddOpen`) so the grid-rows
            transition below actually has something to animate between 0fr and 1fr — collapsing
            by unmounting instead would just cut the content away instantly. */}
        <div className={`hf-bp-quickadd${quickAddOpen ? " open" : ""}`}>
          <div className="hf-bp-quickadd-inner">
            <QuickAddMemberPanel
              key={quickAddKey}
              active={quickAddOpen}
              desktop={isDesktopTablet}
              appointment={appointment}
              bookedClientIds={bookedClientIds}
              onAdded={handleQuickAddSuccess}
              onCancel={closeQuickAdd}
            />
          </div>
        </div>
      </>
    );
  };

  const handleViewProfile = async (client) => {
    try {
      // `GET client/:id` (used by UserDetails.jsx's own fallback) doesn't
      // select address/birthday on the deployed API, so pull the same full
      // list Members screen already uses and find the matching record —
      // guaranteed to carry every field the edit popup needs.
      const clients = await ApiMethods.get("client");
      const fullClient = (clients || []).find(
        (c) => c.id === client.client_id
      );
      setProfileClient(fullClient || client);
    } catch (err) {
      console.log(err);
      showAlert("Greška pri učitavanju profila", "error");
    }
  };

  const handleEditAppointment = (client) => {
    // Guard the action itself, not just the button's disabled styling — a past appointment must
    // stay uneditable even if something else manages to call this (e.g. a future UI path that
    // bypasses the disabled IconButton).
    if (isPastDayAndHour) return;
    // Opens the same shared appointment popup used by "+ Novi termin", in
    // edit mode — preselected with this booking's member/date/time — instead
    // of the old full-calendar "reschedule mode" UI. Deliberately does NOT
    // close this popup: it stays open underneath as the parent modal, and
    // only closes itself (see SchedulerTest.jsx) once the edit is actually
    // saved and moved the appointment to a different slot.
    onEditAppointment(client, appointment);
  };

  // `type` is the trainer's chosen reason ("canceled" | "noshow") from the
  // popup's options — this now goes through the same `auth/delete_bookings`
  // endpoint CardClient.jsx's cancel flow already uses, which sets the
  // booking's status accordingly instead of hard-deleting the row (the old
  // `auth/delete_noshow` call did a real `DELETE`, regardless of its name).
  const deleteAppointment = async (client, type) => {
    if (!client) {
      console.error("deleteAppointment called without client");
      return;
    }
    if (client.timeSlot_id == null) {
      console.error("Client missing timeSlot_id:", client);
      return;
    }

    try {
      console.log("Deleting client:", client, "type:", type);

      const data = {
        timeSlot_id: client.timeSlot_id,
        client_id: client.client_id,
        // Disambiguates this exact booking row from any other active booking the same
        // client has for this same slot (e.g. two packages both used to double-book the
        // same hour) — without it the backend can only match on (timeSlot_id, client_id),
        // which would cancel every matching row instead of just this one.
        booking_id: client.booking_id,
        type: type || "canceled",
      };

      const response = await ApiMethods.post("auth/delete_bookings", data);

      console.log("Response from delete:", response);

      if (response?.message) {
        showAlert(
          type === "noshow"
            ? "Član je označen kao odsutan"
            : "Termin je otkazan",
          "success"
        );
      }

      // A cancellation frees up a package session — refresh this client's cached package
      // rows so Members/the package popup reflect it without a full member reload.
      refreshClientPackages(client.client_id);

      setOpenDeletePopUp(false);
      setClientToDelete(null);
      setOnConfirmFunc(null);
      // No-show is a fast, single-tap workflow — return straight to the calendar instead of
      // staying on the Booked Members list (which "Otkazano" still does, since there's often
      // more than one member left worth reviewing after a cancellation).
      if (type === "noshow") {
        setUsersPopuop(null);
      }
      setBookingAdded();
    } catch (err) {
      console.log("Error deleting:", err);
      showAlert("Greška prilikom otkazivanja termina", "error");
    }
  };

  const onDeleteIconClick = (event, client) => {
    event.stopPropagation();
    // Same defense-in-depth as handleEditAppointment above — a past appointment must not open
    // the Remove confirmation regardless of how this gets triggered.
    if (isPastDayAndHour) return;
    setClientToDelete(client);
    setOnConfirmFunc(() => (type) => deleteAppointment(client, type));
    setOpenDeletePopUp(true);
  };

  const renderMobileCard = () => (
    <div className="card-info hf-popup-card">
      <div className="hf-popup-header-text">
        <h2 className="hf-popup-title">Rezervisani članovi</h2>
        <p className="hf-popup-subtitle">
          {weekdayShort} {startDate.getDate()} · {startTime} ·{" "}
          {usedBookings}/{maxBookings} zakazano
        </p>
      </div>

      <IconButton
        className="hf-popup-close"
        aria-label="Zatvori"
        onClick={() => setUsersPopuop(null)}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <div
        style={{
          marginTop: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "15px",
        }}
        className="user-list"
      >
        {visibleBookingClients.map((client, index) => (
          <div className="user-item-popup hf-booked-row" key={index}>
            <div
              className="hf-booked-identity"
              onClick={() => onSelectMemberWeek && onSelectMemberWeek(client)}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  marginRight: "8px",
                  backgroundColor:
                    client.gender === "Male" ? "#97dfff" : "#FF8E8B",
                  color: "black",
                  fontWeight: "600",
                  fontSize: "11px",
                }}
              >
                {client.first_name
                  ? client.first_name.charAt(0).toUpperCase()
                  : "?"}
                {client.last_name
                  ? client.last_name.charAt(0).toUpperCase()
                  : "?"}
              </Avatar>

              <span className="card-info-details hf-booked-name">
                {client.first_name} {client.last_name}
              </span>
            </div>

            <span className="hf-booked-actions">
              <IconButton
                size="small"
                aria-label="Pogledaj profil"
                onClick={() => handleViewProfile(client)}
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label={
                  isPastDayAndHour
                    ? "Prošli termin ne može da se izmeni"
                    : "Izmeni termin"
                }
                disabled={isPastDayAndHour}
                onClick={() => handleEditAppointment(client)}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label={
                  isPastDayAndHour
                    ? "Prošli termin ne može da se ukloni"
                    : "Ukloni termin"
                }
                disabled={isPastDayAndHour}
                onClick={(event) => onDeleteIconClick(event, client)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: "20px",
          color: "#000",
          fontSize: "13px",
          fontWeight: "600",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {isNormallyFull ? (
          <span>Nema slobodnih mesta</span>
        ) : (
          <span>Preostalo {normalCapacity - usedBookings} termina</span>
        )}
      </div>

      {/* Hidden rather than disabled once the slot is past or already at normal capacity — a
          past/full slot can't take another booking at all, so there's no action left to offer
          here (capacity-full is already explained by "Nema slobodnih mesta" right above). */}
      {renderAddMemberSection()}
    </div>
  );

  const renderDesktopCard = () => (
    <div className="hf-bp-card">
      <div className="hf-bp-header">
        <div className="hf-bp-header-text">
          <h2 className="hf-bp-title">Zakazani članovi</h2>
          <p className="hf-bp-subtitle">
            {weekdayShort} {startDate.getDate()} · {startTime} ·{" "}
            {usedBookings}/{maxBookings} zakazano
          </p>
        </div>
        <IconButton
          className="hf-bp-close"
          aria-label="Zatvori"
          onClick={() => setUsersPopuop(null)}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div className="hf-bp-list">
        {visibleBookingClients.map((client, index) => {
          const initials = `${
            client.first_name ? client.first_name.charAt(0).toUpperCase() : "?"
          }${client.last_name ? client.last_name.charAt(0).toUpperCase() : "?"}`;
          return (
            <div className="hf-bp-row" key={index}>
              <div
                className="hf-bp-identity"
                onClick={() => onSelectMemberWeek && onSelectMemberWeek(client)}
              >
                <Avatar
                  className="hf-bp-avatar"
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor:
                      client.gender === "Male" ? "#97dfff" : "#FF8E8B",
                    color: "black",
                    fontWeight: "600",
                    fontSize: "12px",
                  }}
                >
                  {initials}
                </Avatar>

                <span className="hf-bp-name">
                  {client.first_name} {client.last_name}
                </span>
              </div>

              <span className="hf-bp-actions">
                <Tooltip title="Pogledaj profil">
                  <IconButton
                    className="hf-bp-action"
                    aria-label="Pogledaj profil"
                    onClick={() => handleViewProfile(client)}
                  >
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    isPastDayAndHour
                      ? "Prošli termin ne može da se izmeni"
                      : "Izmeni termin"
                  }
                >
                  {/* MUI Tooltips don't fire hover/focus events on a disabled child directly —
                      wrapping in a span (only needed while disabled) keeps the tooltip working
                      so the "why is this grayed out" reason is still discoverable. */}
                  <span>
                    <IconButton
                      className="hf-bp-action"
                      aria-label={
                        isPastDayAndHour
                          ? "Prošli termin ne može da se izmeni"
                          : "Izmeni termin"
                      }
                      disabled={isPastDayAndHour}
                      onClick={() => handleEditAppointment(client)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    isPastDayAndHour
                      ? "Prošli termin ne može da se ukloni"
                      : "Ukloni termin"
                  }
                >
                  <span>
                    <IconButton
                      className="hf-bp-action hf-bp-action-danger"
                      aria-label={
                        isPastDayAndHour
                          ? "Prošli termin ne može da se ukloni"
                          : "Ukloni termin"
                      }
                      disabled={isPastDayAndHour}
                      onClick={(event) => onDeleteIconClick(event, client)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </span>
            </div>
          );
        })}
      </div>

      {/* Hidden rather than disabled once the slot is past or already at normal capacity — see
          the Mobile card's own version of this button above for why. */}
      {renderAddMemberSection()}
    </div>
  );

  // Booked Members (this card + its own backdrop) is hidden entirely while a child popup —
  // View/Edit Profile or the Remove confirmation — is open, instead of staying stacked
  // underneath it. Each child renders its own full-screen backdrop, so leaving Booked Members
  // mounted too used to double them up and show both cards at once. It reappears automatically
  // once the child closes (`profileClient`/`openDeletePopUp` reset), same as before. Clicking a
  // member row itself doesn't open a child popup here at all — `onSelectMemberWeek` tells the
  // parent (SchedulerTest) to highlight that member's appointments directly in the Calendar
  // grid, which unmounts this whole component (see the `openUsersPopUp != null && !highlightedMember`
  // gate in SchedulerTest.jsx) so the grid is visible underneath.
  const showBookedMembers = !profileClient && !openDeletePopUp;

  return (
    <>
      {showBookedMembers && (
        <div className="hf-bp-overlay">
          {isDesktopTablet ? renderDesktopCard() : renderMobileCard()}
        </div>
      )}

      {openDeletePopUp && handleDeletePopup()}

      {profileClient && (
        <PopupTest
          activeClientInformation={profileClient}
          setActiveClientInformation={applyProfileClientUpdate}
          refresh={setBookingAdded}
          openPopup={() => setProfileClient(null)}
        />
      )}
    </>
  );
};

export default PopupCard;
